import { apiRequest, apiRoutes } from '../../api';
import React, { useState, useEffect } from 'react';
import AddAgentModal from './components/AddAgentModal';
import CompletionScreen from './components/CompletionScreen';
import PropertyDashboard from './components/PropertyDashboard';
import WizardLayout from './components/WizardLayout';
import WizardStepContent from './components/WizardStepContent';
import { createEmptyStepData, SKIPPABLE_PROPERTY_WIZARD_STEPS } from './propertyWizardConfig';
import './PropertyWizard.css';

/**
 * PropertyWizard Component
 * 
 * Main wizard component for property registration following the new API structure.
 * 
 * API Endpoints Used:
 * - POST /api/properties/ - Create new property (step 0)
 * - PATCH /api/properties/{id}/steps/0-ownership/ - Update ownership
 * - PATCH /api/properties/{id}/steps/1-location/ - Update location
 * - PATCH /api/properties/{id}/steps/2-details/ - Update details
 * - POST /api/properties/{id}/steps/3-geo/fetch/ - Fetch geo data
 * - POST /api/properties/{id}/steps/4-images/complete/ - Validate images
 * - PUT /api/properties/{id}/steps/5-plan/ - Upload plan
 * - POST /api/properties/{id}/steps/6-listing/choice/ - Listing choice
 * - POST /api/properties/{id}/steps/7-verify/ - Verify data
 * - POST /api/properties/{id}/steps/8-price/ - Set price
 * - POST /api/properties/{id}/steps/9-schedule/complete/ - Validate schedule
 * - GET /api/properties/{id}/steps/10-summary/ - Get summary
 * - POST /api/properties/{id}/steps/11-finalize/ - Finalize submission
 * - PATCH /api/properties/{id}/steps/12-agent/ - Select agent
 */
function PropertyWizard({ propertyId: initialPropertyId }) {
  // Core state
  const [activeStep, setActiveStep] = useState(0);
  const [propertyId, setPropertyId] = useState(initialPropertyId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [completionStatus, setCompletionStatus] = useState(null);

  // Wizard flow state
  const [, setWantList] = useState(null);
  const [listingId, setListingId] = useState(null);
  const [, setFinalizeResult] = useState(null);

  // Property selection state
  const [showDraftSelection, setShowDraftSelection] = useState(!initialPropertyId);
  const [userProperties, setUserProperties] = useState([]);
  const [userListings, setUserListings] = useState([]);

  // Tabbed dashboard state
  const [activeTab, setActiveTab] = useState('properties');
  const [agentRelations, setAgentRelations] = useState([]);
  const [tabCounts, setTabCounts] = useState({ agents: 0, properties: 0, sale: 0, rent: 0 });

  // Listing edit modal state
  const [editingListing, setEditingListing] = useState(null);

  // Property edit modal state (steps 1–5 only)
  const [editingProperty, setEditingProperty] = useState(null);

  // Listing create modal state (steps 6–11)
  const [creatingListingForProperty, setCreatingListingForProperty] = useState(null);

  // Add agent modal state
  const [showAddAgentModal, setShowAddAgentModal] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);

  // Step data cache
  const [stepData, setStepData] = useState(createEmptyStepData);

  useEffect(() => {
    if (!initialPropertyId) {
      fetchUserProperties();
      fetchAgentRelations();
    } else {
      loadPropertyData(initialPropertyId);
    }
  }, [initialPropertyId]);

  // Helper: unwrap paginated or plain array responses
  const unwrapList = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
  };

  // Fetch properties visible to the current user
  const fetchUserProperties = async () => {
    try {
      const token = localStorage.getItem('access_token');
      // Fetch all properties without status filter to avoid missing any
      const propRes = await apiRequest(apiRoutes.properties.list, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (propRes.ok) {
        const allProperties = unwrapList(await propRes.json());
        setUserProperties(allProperties);
        setTabCounts(prev => ({ ...prev, properties: allProperties.length }));
      }
      try {
        const listingsRes = await apiRequest(apiRoutes.listings.list, { headers: { 'Authorization': `Bearer ${token}` } });
        if (listingsRes.ok) {
          const listings = unwrapList(await listingsRes.json());
          setUserListings(listings);
          setTabCounts(prev => ({
            ...prev,
            sale: listings.filter(l => l.listing_type === 'SALE').length,
            rent: listings.filter(l => l.listing_type === 'RENT').length,
          }));
        }
      } catch { setUserListings([]); }
    } catch (err) {

    }
  };

  // Fetch owner-agent relations where current user is the OWNER
  const fetchAgentRelations = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await apiRequest(apiRoutes.agents.relations, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        const arr = unwrapList(data);
        setAgentRelations(arr);
        setTabCounts(prev => ({ ...prev, agents: arr.length }));
      }
    } catch (err) {

    }
  };

  // Load property data for editing
  const loadPropertyData = async (propId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(apiRoutes.properties.detail(propId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();

        // Populate step data from property
        setStepData(prev => ({
          ...prev,
          1: {
            address: data.address,
            city_id: data.city_id,
            province_id: data.province_id,
            region_id: data.region_id,
            neighborhood_id: data.neighborhood_id,
            postal_code: data.postal_code,
            location: data.location,
          },
          2: {
            name: data.name,
            description: data.description,
            usage: data.usage,
            property_type: data.property_type,
            establish_date: data.establish_date,
            area: data.area,
            amenities: data.amenities || [],
          },
        }));

        // Set active step based on completed_step
        setActiveStep(data.completed_step || 0);

        // Restore listingId if a listing already exists for this property
        try {
          const listingRes = await apiRequest(
            apiRoutes.listings.byProperty(propId),
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (listingRes.ok) {
            const listings = await listingRes.json();
            const arr = Array.isArray(listings) ? listings : listings.results || [];
            if (arr.length > 0) {
              setListingId(arr[0].id);
            }
          }
        } catch { /* non-fatal */ }
      }
    } catch (err) {

      setError('خطا در بارگذاری اطلاعات ملک');
    }
  };

  // Handle new property creation
  const handleNewProperty = () => {
    setPropertyId(null);
    setActiveStep(0);
    setShowDraftSelection(false);
    setCompletionStatus(null);
    setFinalizeResult(null);
    setListingId(null);
    setWantList(null);
    setStepData(createEmptyStepData());
  };

  const handleGoToDashboard = () => {
    setCompletionStatus(null);
    setFinalizeResult(null);
    setPropertyId(null);
    setListingId(null);
    setWantList(null);
    setActiveStep(0);
    setShowDraftSelection(true);
    fetchUserProperties();
    fetchAgentRelations();
  };

  // Handle property deletion
  const handleDeleteProperty = async (propertyId, propertyName) => {
    if (!window.confirm(`آیا از حذف "${propertyName || `ملک ${propertyId}`}" اطمینان دارید؟\n\nتوجه: این عمل قابل بازگشت نیست.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(apiRoutes.properties.detail(propertyId), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('ملک با موفقیت حذف شد');
        // Refresh the properties list
        await fetchUserProperties();
      } else if (response.status === 403) {
        alert('شما مجاز به حذف این ملک نیستید');
      } else {
        alert('خطا در حذف ملک');
      }
    } catch (err) {

      alert('خطا در حذف ملک');
    }
  };

  // Handle listing deletion
  const handleDeleteListing = async (listingId, listingType) => {
    if (!window.confirm(`آیا از حذف این آگهی ${listingType === 'SALE' ? 'فروش' : 'اجاره'} اطمینان دارید؟\n\nتوجه: این عمل قابل بازگشت نیست.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(apiRoutes.listings.detail(listingId), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('آگهی با موفقیت حذف شد');
        // Refresh the properties list
        await fetchUserProperties();
      } else if (response.status === 403) {
        alert('شما مجاز به حذف این آگهی نیستید');
      } else {
        alert('خطا در حذف آگهی');
      }
    } catch (err) {

      alert('خطا در حذف آگهی');
    }
  };

  // Handle agent property assignment
  const handleAssignProperty = async (relationId) => {
    // TODO: Show modal to select properties
    alert('قابلیت انتخاب ملک برای نماینده به زودی اضافه خواهد شد');
  };

  // Handle agent rating
  const handleRateAgent = async (relationId, rating) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(
        apiRoutes.agents.rateAgent(relationId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ rating })
        }
      );

      if (response.ok) {
        alert('امتیاز با موفقیت ثبت شد');
        await fetchAgentRelations();
      } else {
        alert('خطا در ثبت امتیاز');
      }
    } catch (err) {

      alert('خطا در ثبت امتیاز');
    }
  };

  // Handle agent removal
  const handleRemoveAgent = async (relationId, agentName) => {
    if (!window.confirm(`آیا از حذف نماینده "${agentName}" اطمینان دارید؟`)) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(
        apiRoutes.agents.toggleRelationStatus(relationId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ status: 'REJECTED' })
        }
      );

      if (response.ok) {
        alert('نماینده با موفقیت حذف شد');
        await fetchAgentRelations();
      } else {
        alert('خطا در حذف نماینده');
      }
    } catch (err) {

      alert('خطا در حذف نماینده');
    }
  };

  // Handle add new agent
  const handleAddAgent = async () => {
    try {
      const token = localStorage.getItem('access_token');

      // Fetch available agents
      const agentsResponse = await apiRequest(apiRoutes.agents.list, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (agentsResponse.ok) {
        const agents = await agentsResponse.json();
        setAvailableAgents(agents);
        setShowAddAgentModal(true);
      } else {
        alert('خطا در دریافت لیست نمایندگان');
      }
    } catch (err) {

      alert('خطا در دریافت لیست نمایندگان');
    }
  };

  // Handle submit add agent
  const handleSubmitAddAgent = async () => {
    if (!selectedAgent) {
      alert('لطفاً یک نماینده انتخاب کنید');
      return;
    }

    if (selectedProperties.length === 0) {
      alert('لطفاً حداقل یک ملک انتخاب کنید');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(apiRoutes.agents.relations, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          agent: selectedAgent,
          properties: selectedProperties
        })
      });

      if (response.ok) {
        alert('نماینده با موفقیت اضافه شد');
        setShowAddAgentModal(false);
        setSelectedAgent(null);
        setSelectedProperties([]);
        await fetchAgentRelations();
      } else {
        const errorData = await response.json();
        alert(errorData.agent || errorData.properties || errorData.detail || 'خطا در افزودن نماینده');
      }
    } catch (err) {

      alert('خطا در افزودن نماینده');
    }
  };

  // Handle cancel add agent
  const handleCancelAddAgent = () => {
    setShowAddAgentModal(false);
    setSelectedAgent(null);
    setSelectedProperties([]);
  };

  // Toggle property selection
  const togglePropertySelection = (propertyId) => {
    setSelectedProperties(prev => {
      if (prev.includes(propertyId)) {
        return prev.filter(id => id !== propertyId);
      } else {
        return [...prev, propertyId];
      }
    });
  };

  // Handle step submission
  const handleStepSubmit = async (stepDataPayload) => {
    setLoading(true);
    setError(null);

    // Save step data to cache
    setStepData(prev => ({
      ...prev,
      [activeStep]: stepDataPayload
    }));

    try {
      const token = localStorage.getItem('access_token');
      let response;
      let endpoint;

      // Step 0: Create property draft with owner resolved in OwnershipStep
      if (activeStep === 0) {
        let currentPropertyId = propertyId;

        if (!currentPropertyId) {
          const createRes = await apiRequest(apiRoutes.properties.list, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(stepDataPayload), // contains owner_id
          });

          if (!createRes.ok) {
            const err = await createRes.json();
            throw new Error(err.owner_id || err.detail || err.error || 'خطا در ایجاد ملک');
          }

          const createData = await createRes.json();
          currentPropertyId = createData.id || createData.property_id;
          setPropertyId(currentPropertyId);
          response = createRes;
        } else {
          // Re-entering step 0 on existing draft — update ownership
          const ownershipEndpoint = apiRoutes.propertyWizard.step(currentPropertyId, '0-ownership');
          response = await apiRequest(ownershipEndpoint, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(stepDataPayload),
          });
        }
      }
      // Step 1: Location
      else if (activeStep === 1) {
        endpoint = apiRoutes.propertyWizard.step(propertyId, '1-location');
        response = await apiRequest(endpoint, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(stepDataPayload)
        });
      }
      // Step 2: Details
      else if (activeStep === 2) {
        endpoint = apiRoutes.propertyWizard.step(propertyId, '2-details');
        response = await apiRequest(endpoint, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(stepDataPayload)
        });
      }
      // Step 3: Geo fetch
      else if (activeStep === 3) {
        endpoint = apiRoutes.propertyWizard.geospatialFetch(propertyId);
        response = await apiRequest(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(stepDataPayload)
        });
      }
      // Step 4: Images complete
      else if (activeStep === 4) {
        endpoint = apiRoutes.propertyWizard.completeStep(propertyId, '4-images');
        response = await apiRequest(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(stepDataPayload)
        });
      }
      // Step 5: Plan upload
      else if (activeStep === 5) {
        // Check if plan is already saved (uploaded via FloorPlanStep)
        if (stepDataPayload.floor_plan_saved && stepDataPayload.floor_plan_image_url) {
          // Plan already uploaded, just mark step as complete

          response = { ok: true };
        } else {
          // Legacy flow: upload plan via wizard
          endpoint = apiRoutes.propertyWizard.plan(propertyId);

          // Validate that we have the plan file
          if (!stepDataPayload.plan) {
            throw new Error('لطفاً ابتدا پلان را ذخیره کنید');
          }

          const formData = new FormData();
          formData.append('plan', stepDataPayload.plan);

          response = await apiRequest(endpoint, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });
        }
      }
      // Step 6: Listing choice
      else if (activeStep === 6) {
        endpoint = apiRoutes.propertyWizard.listingChoice(propertyId);
        response = await apiRequest(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            create_listing: stepDataPayload.create_listing,
            listing_type: stepDataPayload.listing_type,
            available_from: stepDataPayload.available_from
          })
        });

        if (response.ok) {
          const data = await response.json();
          setWantList(data.create_listing);

          // If user wants to create listing, create it and update details
          if (data.create_listing && data.listing_type) {
            const listing = await createListing(data.listing_type, data.available_from);

            // Update listing details based on type
            if (listing && listing.id) {
              if (data.listing_type === 'RENT' && stepDataPayload.rent_details) {
                await updateListingDetails(listing.id, 'rent', stepDataPayload.rent_details);
              } else if (data.listing_type === 'SALE' && stepDataPayload.sale_details) {
                await updateListingDetails(listing.id, 'sale', stepDataPayload.sale_details);
              }
            }
          }

          // Jump to appropriate next step
          if (data.next_step) {
            setActiveStep(data.next_step);
            setLoading(false);
            return;
          }
        }
      }
      // Step 7: Verification
      else if (activeStep === 7) {
        endpoint = apiRoutes.propertyWizard.step(propertyId, '7-verify');
        response = await apiRequest(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({})
        });

        if (response.ok) {
          const verifyData = await response.json();
          if (!verifyData.ok) {
            const issueMessages = verifyData.issues?.map(i => i.message).join('\n') || 'اطلاعات ملک ناقص است';
            throw new Error(issueMessages);
          }
          // Step advanced on backend, move forward
          setActiveStep(prev => prev + 1);
          setLoading(false);
          return;
        }
      }
      // Step 8: Price
      else if (activeStep === 8) {
        // Resolve listingId if not set (e.g. resuming wizard)
        let currentListingId = listingId;
        if (!currentListingId) {
          const listingRes = await apiRequest(
            apiRoutes.listings.byProperty(propertyId),
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (listingRes.ok) {
            const listings = await listingRes.json();
            const arr = Array.isArray(listings) ? listings : listings.results || [];
            if (arr.length > 0) {
              currentListingId = arr[0].id;
              setListingId(currentListingId);
            }
          }
        }
        if (!currentListingId) throw new Error('شناسه آگهی یافت نشد');

        endpoint = apiRoutes.propertyWizard.step(propertyId, '8-price');
        response = await apiRequest(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ ...stepDataPayload, listing_id: currentListingId })
        });
      }
      // Step 9: Schedule complete
      else if (activeStep === 9) {
        // Map day names to weekday integers (0=Monday ... 6=Sunday, Django convention)
        const dayToWeekday = {
          MONDAY: 0, TUESDAY: 1, WEDNESDAY: 2, THURSDAY: 3,
          FRIDAY: 4, SATURDAY: 5, SUNDAY: 6
        };

        const slots = stepDataPayload.visiting_schedule || [];
        const currentListingId = listingId;

        if (!currentListingId) {
          throw new Error('شناسه آگهی یافت نشد');
        }

        if (slots.length === 0) {
          throw new Error('لطفاً حداقل یک بازه زمانی انتخاب کنید');
        }

        // Delete existing schedules first, then recreate
        const existingRes = await apiRequest(
          apiRoutes.listings.schedules(currentListingId),
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (existingRes.ok) {
          const existing = await existingRes.json();
          await Promise.all(existing.map(s =>
            apiRequest(apiRoutes.listings.schedule(currentListingId, s.id), {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
            })
          ));
        }

        // POST each selected slot
        for (const slotId of slots) {
          // slotId format: "SATURDAY-08:00-10:00"
          // Split on first '-' to get day, then the rest is "08:00-10:00"
          const dashIdx = slotId.indexOf('-');
          const day = slotId.substring(0, dashIdx);
          const times = slotId.substring(dashIdx + 1); // "08:00-10:00"
          const [startTime, endTime] = times.split('-');
          await apiRequest(
            apiRoutes.listings.schedules(currentListingId),
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({
                weekday: dayToWeekday[day],
                start_time: startTime,
                end_time: endTime,
              })
            }
          );
        }

        endpoint = apiRoutes.propertyWizard.completeStep(propertyId, '9-schedule');
        response = await apiRequest(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ listing_id: currentListingId })
        });
      }
      // Step 10: Summary - complete step with name and description
      else if (activeStep === 10) {
        endpoint = apiRoutes.propertyWizard.completeStep(propertyId, '10-summary');
        response = await apiRequest(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: stepDataPayload.name,
            description: stepDataPayload.description || ''
          })
        });
      }
      // Step 11: Finalize
      else if (activeStep === 11) {
        endpoint = apiRoutes.propertyWizard.finalize(propertyId);
        response = await apiRequest(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(stepDataPayload)
        });

        if (response.ok) {
          // Wizard complete; promote listing from DRAFT to HIDDEN
          if (listingId) {
            await apiRequest(apiRoutes.listings.detail(listingId), {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ lifecycle_status: 'HIDDEN' })
            });
          }
          const finalizeData = await response.json();
          setFinalizeResult(finalizeData); // { listed, reason, lifecycle_status, ... }
          setCompletionStatus('published');
          setLoading(false);
          return;
        }
      }
      // Step 12: Agent selection (DISABLED — agent assignment is now systematic)
      // else if (activeStep === 12) { ... }

      if (!response.ok) {
        const errorData = await response.json();

        const msg = errorData.detail
          || (Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors[0] : null)
          || (typeof errorData === 'object' ? JSON.stringify(errorData) : null)
          || 'خطا در ذخیره اطلاعات';
        throw new Error(msg);
      }

      // Move to next step
      setActiveStep(prev => prev + 1);
    } catch (err) {

      setError(err.message || 'خطا در ذخیره اطلاعات');
    } finally {
      setLoading(false);
    }
  };

  // Create listing
  const createListing = async (listingType, availableFrom) => {
    try {
      const token = localStorage.getItem('access_token');

      const payload = {
        property: propertyId,
        listing_type: listingType,
        available_from: availableFrom,
        listed_by_type: 'OWNER',
        lifecycle_status: 'DRAFT',
        availability_status: 'AVAILABLE'
      };

      const response = await apiRequest(apiRoutes.listings.list, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();

        setListingId(data.id);
        return data;
      } else {
        const errorData = await response.json();

        // Show detailed error message
        const errorMessage = errorData.detail || 
                            JSON.stringify(errorData) || 
                            'Failed to create listing';
        throw new Error(errorMessage);
      }
    } catch (err) {

      throw err;
    }
  };

  // Update listing details (rent or sale)
  const updateListingDetails = async (listingId, type, details) => {
    try {
      const token = localStorage.getItem('access_token');
      const endpoint = type === 'rent'
        ? apiRoutes.listings.rentDetails(listingId)
        : apiRoutes.listings.saleDetails(listingId);

      const response = await apiRequest(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(details)
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        // Don't throw - details can be updated later
        return null;
      }
    } catch (err) {

      // Don't throw - details can be updated later
      return null;
    }
  };

  // Handle previous step
  const handlePreviousStep = () => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
      setError(null);
    }
  };

  const handleSkipStep = async () => {
    if (!propertyId || !SKIPPABLE_PROPERTY_WIZARD_STEPS.has(activeStep)) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest(apiRoutes.propertyWizard.skipStep(propertyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ step: activeStep })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.step || 'خطا در رد کردن این مرحله');
      }

      setStepData(prev => ({
        ...prev,
        [activeStep]: { ...prev[activeStep], skipped: true }
      }));
      setActiveStep(prev => prev + 1);
    } catch (err) {
      setError(err.message || 'خطا در رد کردن این مرحله');
    } finally {
      setLoading(false);
    }
  };

  const dashboardVisible = showDraftSelection || completionStatus === 'published';
  let content;

  if (showDraftSelection) {
    content = (
      <PropertyDashboard
        activeTab={activeTab}
        counts={tabCounts}
        agentRelations={agentRelations}
        properties={userProperties}
        listings={userListings}
        editingListing={editingListing}
        editingProperty={editingProperty}
        creatingListingForProperty={creatingListingForProperty}
        onTabChange={setActiveTab}
        onAddAgent={handleAddAgent}
        onAssignProperty={handleAssignProperty}
        onRateAgent={handleRateAgent}
        onRemoveAgent={handleRemoveAgent}
        onEditListing={setEditingListing}
        onEditProperty={setEditingProperty}
        onCreateListing={setCreatingListingForProperty}
        onDeleteListing={handleDeleteListing}
        onDeleteProperty={handleDeleteProperty}
        onNewProperty={handleNewProperty}
        onRefresh={fetchUserProperties}
      />
    );
  } else if (completionStatus === 'published') {
    content = <CompletionScreen onBack={handleGoToDashboard} />;
  } else {
    content = (
      <WizardStepContent
        activeStep={activeStep}
        stepData={stepData}
        propertyId={propertyId}
        listingId={listingId}
        onSubmit={handleStepSubmit}
        onPrevious={handlePreviousStep}
      />
    );
  }

  return (
    <>
      <WizardLayout
        activeStep={activeStep}
        dashboard={dashboardVisible}
        error={error}
        loading={loading}
        onShowDashboard={() => setShowDraftSelection(true)}
        canSkipStep={SKIPPABLE_PROPERTY_WIZARD_STEPS.has(activeStep)}
        onSkipStep={handleSkipStep}
      >
        {content}
      </WizardLayout>

      {showAddAgentModal && (
        <AddAgentModal
          agents={availableAgents}
          properties={userProperties}
          selectedAgent={selectedAgent}
          selectedProperties={selectedProperties}
          onAgentChange={setSelectedAgent}
          onPropertyToggle={togglePropertySelection}
          onCancel={handleCancelAddAgent}
          onSubmit={handleSubmitAddAgent}
        />
      )}
    </>
  );
}

export default PropertyWizard;
